/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import 'normalize.css';

import './semantic_2.4.1.min.css';

import './App.css';
import './ThirdParty.css';

import { MarketingModal, useDisclosure } from '@ouster/webviz-react-components';

import {
  ThemeProvider as MaterialThemeProvider,
  createTheme,
} from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Popover } from '@typeform/embed-react';
import React, { useEffect, useMemo, useState } from 'react';
import { IoChatboxEllipsesOutline } from 'react-icons/io5';
import { BrowserRouter } from 'react-router-dom';

import HandoverModal from './components/adminDashboard/provision/HandoverModal';
import ConfirmDialog from './components/ConfirmDialog';
import { Toast } from './components/Toast';
import { KeycloakProvider } from './KeycloakContext';
import { useStoreActions, useStoreState } from './reducers';
import { AppRoutes } from './routes';
import { SDKSensorInfoAugmented } from './types';
import { isAdminLevelRole } from './utils/roles';

const queryClient = new QueryClient();

const App: React.FC = () => {
  const materialDarkTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: 'dark',
          primary: {
            // mui didn't support variable here, hence, color code is passed here
            main: '#5e79fb',
          },
        },
        components: {
          MuiChip: {
            styleOverrides: {
              colorSuccess: {
                backgroundColor: '#18653c', // custom green
                color: '#fff',
              },
              colorError: {
                backgroundColor: '#E53E3E', // custom red
                color: '#fff',
              },
              colorInfo: {
                backgroundColor: '#3182CE', // custom blue
                color: '#fff',
              },
              colorWarning: {
                backgroundColor: '#f6e865', // custom orange
                color: '#black',
              },
            },
          },
        },
      }),
    []
  );

  /* 
    This is to detect logout in another tab
    When user logout in another tab, we want to detect it and refresh the current tab
    keycloak logout is a global logout, it kills all the session in all tabs however, visually, it doesn't appear like that until user refresh and do any action. Hence, we trigger a refresh when logout is detected in another tab.
  */
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'logout-event') {
        window.location.href = '/login';
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const { appUser, curUserOrg } = useStoreState(
    (storeState) => storeState.user
  );
  const driveInfo = useStoreState((storeState) => storeState.drive.driveInfo);
  const vizMetadata = useStoreState(
    (storeState) =>
      storeState.vizScene.metadata as SDKSensorInfoAugmented[] | undefined
  );
  const { updateUserMarketingPreference, fetchUserOrgs } = useStoreActions(
    (actions) => actions.user
  );
  const { completeOrgHandover } = useStoreActions(
    (actions) => actions.organization
  );
  const [isMarketingModalOpen, setIsMarketingModalOpen] = useState(false);

  const {
    isOpen: isHandoverModalOpen,
    onOpen: onHandOverModalOpen,
    onClose: onHandOverModalClose,
  } = useDisclosure();

  const shouldShowHandoverModal = useMemo(
    () =>
      curUserOrg?.is_provisioned &&
      !curUserOrg?.handed_over_at &&
      isAdminLevelRole(curUserOrg?.role) &&
      !appUser?.email?.endsWith('@ouster.io'),
    [curUserOrg, appUser]
  );

  const shouldShowMarketingModal = useMemo(
    () => appUser && appUser.email_opt_out === null,
    [appUser]
  );

  // Show HandoverModal first if needed
  useEffect(() => {
    if (shouldShowHandoverModal && !isHandoverModalOpen) {
      onHandOverModalOpen();
    }
  }, [shouldShowHandoverModal, isHandoverModalOpen, onHandOverModalOpen]);

  // Show MarketingModal only when HandoverModal is not showing
  useEffect(() => {
    if (
      shouldShowMarketingModal &&
      !shouldShowHandoverModal &&
      !isHandoverModalOpen &&
      !isMarketingModalOpen &&
      curUserOrg
    ) {
      setIsMarketingModalOpen(true);
    }
  }, [
    shouldShowMarketingModal,
    shouldShowHandoverModal,
    isHandoverModalOpen,
    isMarketingModalOpen,
    curUserOrg,
  ]);

  const handleHandoverClose = () => {
    onHandOverModalClose();
  };

  const handleMarketingSave = async (optIn: boolean) => {
    try {
      if (appUser && appUser.identity_id) {
        await updateUserMarketingPreference({
          identity_id: appUser.identity_id,
          opt_out: !optIn,
        });
        setIsMarketingModalOpen(false);
      }
    } catch (error) {
      console.error('Failed to update marketing preferences:', error);
    }
  };

  const handleHandoverConfirm = async () => {
    try {
      if (curUserOrg?.id && appUser?.identity_id) {
        await completeOrgHandover(curUserOrg.id);
        await fetchUserOrgs(appUser.identity_id);
        onHandOverModalClose();
      }
    } catch (error) {
      console.error('Failed to complete handover:', error);
    }
  };

  const browserVersion = useMemo(() => {
    if (typeof navigator === 'undefined') {
      return 'unknown';
    }

    const ua = navigator.userAgent;
    const patterns: RegExp[] = [
      /Edg\/(\d+(?:\.\d+)?)/i,
      /Chrome\/(\d+(?:\.\d+)?)/i,
      /CriOS\/(\d+(?:\.\d+)?)/i,
      /Firefox\/(\d+(?:\.\d+)?)/i,
      /FxiOS\/(\d+(?:\.\d+)?)/i,
      /Version\/(\d+(?:\.\d+)?).*Safari/i,
    ];

    const matchedPattern = patterns.find((pattern) => pattern.test(ua));
    if (!matchedPattern) {
      return 'unknown';
    }

    return ua.match(matchedPattern)?.[1] ?? 'unknown';
  }, []);

  const deviceType = useMemo(() => {
    if (typeof navigator === 'undefined') {
      return 'unknown';
    }

    const ua = navigator.userAgent;
    if (/Tablet|iPad|PlayBook|Silk/i.test(ua)) {
      return 'tablet';
    }

    if (/Mobi|Android|iPhone|iPod/i.test(ua)) {
      return 'mobile';
    }

    return 'desktop';
  }, []);

  const screenResolution = useMemo(() => {
    if (typeof window === 'undefined') {
      return 'unknown';
    }

    return `${window.screen.width}x${window.screen.height}`;
  }, []);

  const driveRouteMatch = window.location.pathname.match(
    /^\/organization\/([^/]+)\/drive\/([^/?#]+)/
  );
  const routeOrgId = driveRouteMatch?.[1];
  const routeDriveId = driveRouteMatch?.[2];
  const compactJsonMetadata = useMemo(() => {
    if (Array.isArray(vizMetadata) && vizMetadata.length > 0) {
      return vizMetadata.map((sensorMetadata) => ({
        sn: sensorMetadata.sn,
        mode: sensorMetadata.mode,
        product_line: sensorMetadata.prod_line,
        prod_pn: sensorMetadata.prod_pn,
        prod_resolution: sensorMetadata.prod_resolution,
      }));
    }

    if (
      Array.isArray(driveInfo?.sensorInfos) &&
      driveInfo?.sensorInfos?.length &&
      driveInfo?.sensorInfos?.length > 0
    ) {
      return driveInfo?.sensorInfos.map((sensorInfo) => ({
        sn: sensorInfo.sdkProductSerialNumber,
        mode: sensorInfo.sdkLidarMode,
        product_line: sensorInfo.sdkProductLine,
        prod_pn: sensorInfo.sdkProductPartNumber,
        prod_resolution:
          sensorInfo.sdkProductResolution !== null
            ? String(sensorInfo.sdkProductResolution)
            : null,
      }));
    }

    return null;
  }, [vizMetadata, driveInfo?.sensorInfos]);

  const driveContext =
    driveRouteMatch && (driveInfo || routeDriveId)
      ? {
          id: driveInfo?.id ?? routeDriveId,
          orgId: driveInfo?.organizationId ?? routeOrgId,
          recordingTitle: driveInfo?.recordingTitle ?? null,
          dataCategory: driveInfo?.dataCategory ?? null,
          vizStatus: driveInfo?.visualization_status ?? null,
          osfStatus: driveInfo?.osfGenerationStatus ?? null,
          jsonMetadata: compactJsonMetadata,
        }
      : null;

  const popoverHiddenFields = {
    name: appUser?.name ?? '',
    email: appUser?.email ?? '',
    from: window.location.pathname,
    platform: 'OSW',
    browser_version: browserVersion,
    device_type: deviceType,
    screen_resolution: screenResolution,
    ...(driveContext
      ? {
          drive_id: String(driveContext.id),
          org_id: String(driveContext.orgId),
          drive_info: JSON.stringify(driveContext),
        }
      : {}),
  };

  const popoverIframeProps = {
    credentialless: true,
  };

  const isSafariBrowser = useMemo(() => {
    if (typeof navigator === 'undefined') {
      return false;
    }

    const { userAgent } = navigator;
    return (
      /Safari/i.test(userAgent) &&
      !/Chrome|CriOS|Chromium|Edg|EdgiOS|Firefox|FxiOS|OPR|Opera|Android/i.test(
        userAgent
      )
    );
  }, []);

  const safariPopoverUrl = `https://form.typeform.com/to/XfXOrUKH?${new URLSearchParams(
    popoverHiddenFields
  ).toString()}`;

  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <KeycloakProvider>
        <MaterialThemeProvider theme={materialDarkTheme}>
          <QueryClientProvider client={queryClient}>
            <AppRoutes />
            <ConfirmDialog />
            <MarketingModal
              isOpen={isMarketingModalOpen}
              handleClose={() => setIsMarketingModalOpen(false)}
              handleSave={handleMarketingSave}
            />
            <HandoverModal
              isOpen={isHandoverModalOpen}
              handleClose={handleHandoverClose}
              handleHandoverConfirm={handleHandoverConfirm}
            />
            {isSafariBrowser ? (
              <a
                href={safariPopoverUrl}
                target="_blank"
                rel="noreferrer noopener"
                style={{
                  position: 'fixed',
                  bottom: 12,
                  right: 12,
                  zIndex: 1000,
                  backgroundColor: '#5e79fb',
                  color: '#fff',
                  borderRadius: 999,
                  padding: '12px',
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                <IoChatboxEllipsesOutline size={24} />
              </a>
            ) : (
              <Popover
                id="XfXOrUKH"
                hidden={popoverHiddenFields}
                height={600}
                buttonColor="#5e79fb"
                iframeProps={popoverIframeProps}
              />
            )}
            <Toast />
          </QueryClientProvider>
        </MaterialThemeProvider>
      </KeycloakProvider>
    </BrowserRouter>
  );
};

export default App;
